import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Loader2, Briefcase } from "lucide-react";
import { api } from "../lib/api";
import { slugify } from "../lib/utils";
import GEO from "../data/portugalMap.json";

// Distritos agrupados por região (continente + ilhas)
const REGIONS = [
  { key: "norte", label: "Norte", color: "from-blue-500/10 to-blue-500/0", dot: "bg-blue-500",
    districts: ["Braga", "Bragança", "Porto", "Viana do Castelo", "Vila Real"] },
  { key: "centro", label: "Centro", color: "from-emerald-500/10 to-emerald-500/0", dot: "bg-emerald-500",
    districts: ["Aveiro", "Castelo Branco", "Coimbra", "Guarda", "Leiria", "Viseu"] },
  { key: "lisboa-tejo", label: "Lisboa e Vale do Tejo", color: "from-amber-500/10 to-amber-500/0", dot: "bg-amber-500",
    districts: ["Lisboa", "Santarém", "Setúbal"] },
  { key: "alentejo", label: "Alentejo", color: "from-orange-500/10 to-orange-500/0", dot: "bg-orange-500",
    districts: ["Beja", "Évora", "Portalegre"] },
  { key: "algarve", label: "Algarve", color: "from-rose-500/10 to-rose-500/0", dot: "bg-rose-500",
    districts: ["Faro"] },
  { key: "ilhas", label: "Ilhas", color: "from-sky-500/10 to-sky-500/0", dot: "bg-sky-500",
    districts: ["Açores", "Madeira"] },
];

// Escala de cor (log) — 0 vagas = cinza; mais vagas = âmbar mais intenso
function fillFor(count, max) {
  if (!count) return "rgba(148,163,184,0.30)";
  const t = max > 1 ? Math.log(count + 1) / Math.log(max + 1) : 1;
  const alpha = 0.28 + 0.72 * t;
  return `rgba(251,191,36,${alpha.toFixed(2)})`;
}

function DistrictPill({ name, count, dotColor, active, onHover }) {
  const to = `/empregos-em/${slugify(name)}`;
  const disabled = !count;
  const cls = disabled
    ? "bg-slate-50 border-slate-200 text-slate-400"
    : active
      ? "bg-amber-50 border-amber-400 shadow-sm text-slate-900"
      : "bg-white border-slate-200 hover:border-slate-400 hover:shadow-sm text-slate-800";
  return (
    <Link
      to={to}
      data-testid={`map-district-${slugify(name)}`}
      onMouseEnter={() => onHover(name)}
      onMouseLeave={() => onHover(null)}
      className={`group inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${cls}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
      <span>{name}</span>
      <span className={`inline-flex items-center rounded-full px-1.5 min-w-[22px] justify-center text-[10px] font-bold ${
        disabled ? "bg-slate-100 text-slate-400" : "bg-slate-900 text-white group-hover:bg-amber-400 group-hover:text-slate-900"
      }`}>
        {count || 0}
      </span>
    </Link>
  );
}

function Shape({ shape, count, max, active, onHover, onClick }) {
  return (
    <path
      d={shape.d}
      data-testid={`map-shape-${slugify(shape.name)}`}
      role="link"
      tabIndex={0}
      aria-label={`${shape.name}: ${count || 0} vagas`}
      fill={active ? "rgb(251,191,36)" : fillFor(count, max)}
      stroke={active ? "#fff" : "rgba(15,23,42,0.85)"}
      strokeWidth={active ? 1.6 : 0.8}
      strokeLinejoin="round"
      className="cursor-pointer transition-[fill] duration-200 outline-none focus-visible:stroke-white"
      onMouseEnter={() => onHover(shape.name)}
      onMouseLeave={() => onHover(null)}
      onFocus={() => onHover(shape.name)}
      onBlur={() => onHover(null)}
      onClick={() => onClick(shape.name)}
      onKeyDown={(e) => { if (e.key === "Enter") onClick(shape.name); }}
    />
  );
}

function IslandInset({ geo, label, count, max, active, onHover, onClick, testid }) {
  const shape = geo.shapes[0];
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border px-2 pt-1.5 pb-1 transition ${active ? "border-amber-400/80 bg-white/10" : "border-white/10 bg-white/5"}`}
    >
      <div className="flex items-center justify-between gap-2 text-[10px] font-semibold text-slate-300">
        <span>{label}</span>
        <span className="text-amber-300">{count || 0}</span>
      </div>
      <svg viewBox={`-4 -4 ${geo.width + 8} ${geo.height + 8}`} className="w-full h-auto">
        <Shape shape={shape} count={count} max={max} active={active} onHover={onHover} onClick={onClick} />
      </svg>
    </div>
  );
}

/**
 * Mapa Interativo de Portugal — SVG real (CAOP) com os 18 distritos do continente
 * + Açores e Madeira. Cada distrito é colorido pela quantidade de vagas ativas e
 * é clicável (leva a /empregos-em/{slug}).
 */
export function PortugalMap({ className = "" }) {
  const navigate = useNavigate();
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hover, setHover] = useState(null);
  const [activeTotal, setActiveTotal] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/stats/districts")
      .then((r) => {
        if (!alive) return;
        const map = {};
        (r.data.districts || []).forEach((d) => { map[d.district] = d.count; });
        setCounts(map);
      })
      .catch(() => setCounts({}))
      .finally(() => { if (alive) setLoading(false); });
    // Total real de vagas ativas (inclui vagas remotas/sem distrito)
    api.get("/stats/public").then((r) => { if (alive) setActiveTotal(r.data.active_jobs); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const districtSum = counts ? Object.values(counts).reduce((a, b) => a + b, 0) : 0;
  const totalActive = Math.max(activeTotal || 0, districtSum);
  const max = useMemo(() => (counts ? Math.max(1, ...Object.values(counts)) : 1), [counts]);
  const go = (name) => navigate(`/empregos-em/${slugify(name)}`);
  const c = (name) => counts?.[name] || 0;
  const { mainland, acores, madeira } = GEO;

  return (
    <section
      data-testid="portugal-map"
      className={`bg-white rounded-3xl border border-black/5 shadow-sm p-5 sm:p-8 ${className}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-500 mb-1.5">Explora por região</p>
          <h2 className="font-heading font-black tracking-tighter text-2xl sm:text-3xl text-slate-900">
            Encontra vagas onde vives
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {loading ? (
              <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> A carregar…</span>
            ) : (
              <>Clica num distrito para veres as {totalActive.toLocaleString("pt-PT")} vagas ativas em Portugal.</>
            )}
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-500">
          <MapPin className="w-3.5 h-3.5" /> 18 distritos + Açores e Madeira
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Mapa SVG real */}
        <div
          data-testid="portugal-map-svg-panel"
          className="lg:col-span-2 relative rounded-2xl bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 overflow-hidden p-5"
        >
          <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
            backgroundImage: "radial-gradient(circle at 30% 25%, rgb(251 191 36) 0%, transparent 50%), radial-gradient(circle at 60% 70%, rgb(56 189 248) 0%, transparent 55%)"
          }} />

          <div className="relative z-10 flex gap-4">
            {/* Coluna esquerda: total + ilhas + legenda */}
            <div className="flex flex-col justify-between w-[42%] min-w-[120px]">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-400/90 mb-2">Portugal</p>
                <p className="font-heading font-black tracking-tighter text-3xl sm:text-4xl text-white leading-none" data-testid="map-total">
                  {loading ? "…" : totalActive.toLocaleString("pt-PT")}
                </p>
                <p className="text-sm text-slate-300 mt-1">vagas ativas hoje</p>
                <div
                  className="mt-4 min-h-[44px] rounded-xl bg-white/5 border border-white/10 px-3 py-2"
                  data-testid="map-hover-info"
                >
                  {hover ? (
                    <>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold">Distrito</p>
                      <p className="text-sm font-bold text-white leading-tight">{hover}</p>
                      <p className="text-xs text-amber-300 font-semibold">{c(hover).toLocaleString("pt-PT")} vaga{c(hover) !== 1 ? "s" : ""}</p>
                    </>
                  ) : (
                    <p className="text-[11px] text-slate-400 leading-snug">Passa o rato (ou toca) num distrito para ver as vagas.</p>
                  )}
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <IslandInset geo={acores} label="Açores" count={c("Açores")} max={max} active={hover === "Açores"}
                  onHover={setHover} onClick={go} testid="map-inset-acores" />
                <IslandInset geo={madeira} label="Madeira" count={c("Madeira")} max={max} active={hover === "Madeira"}
                  onHover={setHover} onClick={go} testid="map-inset-madeira" />
                <div className="flex items-center gap-2 pt-1">
                  <span className="text-[10px] text-slate-400">menos</span>
                  <span className="h-1.5 flex-1 rounded-full" style={{ background: "linear-gradient(90deg, rgba(251,191,36,0.28), rgba(251,191,36,1))" }} />
                  <span className="text-[10px] text-slate-400">mais</span>
                </div>
              </div>
            </div>

            {/* Continente */}
            <div className="flex-1 flex items-center justify-center">
              <svg
                viewBox={`-6 -6 ${mainland.width + 12} ${mainland.height + 12}`}
                className="w-full max-h-[440px] h-auto drop-shadow-[0_8px_24px_rgba(251,191,36,0.12)]"
                role="img"
                aria-label="Mapa de Portugal continental por distrito"
                data-testid="portugal-map-svg"
              >
                {mainland.shapes.map((s) => (
                  <Shape key={s.name} shape={s} count={c(s.name)} max={max} active={hover === s.name}
                    onHover={setHover} onClick={go} />
                ))}
              </svg>
            </div>
          </div>
        </div>

        {/* Grelha de regiões com pills clicáveis */}
        <div className="lg:col-span-3 space-y-4">
          {REGIONS.map((r) => {
            const regionTotal = r.districts.reduce((sum, d) => sum + c(d), 0);
            return (
              <div key={r.key} className={`rounded-2xl border border-slate-100 bg-gradient-to-br ${r.color} p-4`}>
                <div className="flex items-center justify-between gap-3 mb-2.5">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${r.dot}`} />
                    <p className="text-xs font-black tracking-tight text-slate-900 uppercase">{r.label}</p>
                  </div>
                  <p className="text-[11px] font-semibold text-slate-500 inline-flex items-center gap-1">
                    <Briefcase className="w-3 h-3" /> {regionTotal.toLocaleString("pt-PT")} vaga{regionTotal !== 1 ? "s" : ""}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {r.districts.map((d) => (
                    <DistrictPill key={d} name={d} count={c(d)} dotColor={r.dot} active={hover === d} onHover={setHover} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default PortugalMap;
